import React, { useState } from "react";
import {
  Typography, TextField, Button, IconButton, CircularProgress,
  Box, Card, CardContent, Divider, makeStyles, Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon,
} from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { getRoles, createRoles, updateRoles } from "../../store/action";

const useStyles = makeStyles((theme) => ({
  sectionHeader: { display: "flex", alignItems: "center", marginBottom: theme.spacing(2) },
  sectionIcon: { marginRight: theme.spacing(1), color: theme.palette.primary.main },
  formCard: { marginTop: theme.spacing(3), marginBottom: theme.spacing(2), borderRadius: 12, backgroundColor: theme.palette.thead, boxShadow: "0 3px 10px rgba(0,0,0,0.08)" },
  roleCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    transition: "all 0.25s ease",
    cursor: "pointer",
    "&:hover": { transform: "translateY(-3px)", boxShadow: "0 6px 16px rgba(0,0,0,0.12)" },
    "&.selected": {
      borderLeft: `5px solid ${theme.palette.primary.main}`, backgroundColor: theme.palette.sidebar
    },
    background: "none",
  },
  emptyState: { textAlign: "center", padding: theme.spacing(5), color: theme.palette.text.secondary },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 180 },
  createForm: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(1) },
  roleTitle: { fontWeight: 600, fontSize: "1rem", color: theme.palette.listItem },
  roleId: { fontSize: "0.75rem", color: theme.palette.listItem },
  rolesList: {
    maxHeight: "52vh",
    overflowY: "auto",
    paddingRight: 8,
    background: theme.palette.grey[1000],
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1)
  },
  paper: {
    background: "none"
  }
}));

const RoleList = ({ addPermission, onSelectRole, selectedRole, onDeleteRole, onCreateRole, onCloneRoleChange, setSelectedRole }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getRolesItem, loading } = useSelector((state) => state.roles);

  const [newRoleName, setNewRoleName] = useState("");
  const [editingRole, setEditingRole] = useState(null);
  const [editName, setEditName] = useState("");
  const [cloneRoleId, setCloneRoleId] = useState(null);

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    const res = await dispatch(createRoles({ name: newRoleName }));
    if (res.success) {
      setNewRoleName("");
      setCloneRoleId(null);
      if (cloneRoleId) addPermission(res.data);
      await dispatch(getRoles());
      onCreateRole?.({ ...res?.data?.data, clone_role_id: cloneRoleId });
    }
  };

  const handleUpdateRole = async (role) => {
    if (!editName.trim()) return;
    const res = await dispatch(updateRoles({ id: role.id, name: editName }));
    if (res?.success) {
      setEditingRole(null);
      setEditName("");
      if (selectedRole?.id === role.id) onSelectRole({ ...role, name: editName });
      await dispatch(getRoles());
    }
  };

  if (loading) {
    return <div className={classes.loadingContainer}><CircularProgress /></div>;
  }

  return (
    <div>
      {/* Header */}
      <div className={classes.sectionHeader}>
        <SecurityIcon className={classes.sectionIcon} />
        <Typography variant="h6">Roles</Typography>
      </div>
      <Divider />

      {/* Create Role */}
      <Card className={classes.formCard}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Create New Role</Typography>
          <Autocomplete
            options={getRolesItem?.data || []}
            getOptionLabel={(option) => option?.name || ""}
            value={cloneRoleId}
            onChange={(_, newValue) => {
              setCloneRoleId(newValue);
              onCloneRoleChange?.(newValue);
              setSelectedRole(null);
            }}
            renderInput={(params) => <TextField {...params} size="small" placeholder="Clone permissions from" variant="outlined" />}
          />
          <div className={classes.createForm}>
            <TextField
              placeholder="Enter role name"
              fullWidth
              variant="outlined"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleCreateRole()}
            />
            <Button variant="contained" color="primary" size="small" onClick={handleCreateRole} disabled={!newRoleName.trim()}>
              <AddIcon />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Roles List */}
      <Box mt={2} className={classes.rolesList}>
        {getRolesItem?.data?.length ? (
          getRolesItem.data
            .filter((role) => role.role_name !== "SUPERADMIN")
            .map((role) => (
              <Card
                key={role.id}
                variant="outlined"
                className={`${classes.roleCard} ${selectedRole?.id === role.id ? "selected" : ""}`}
                onClick={() => onSelectRole(role)}
              >
                <CardContent >
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    {editingRole?.id === role.id ? (
                      <TextField
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleUpdateRole(role)}
                        autoFocus size="small"
                      />
                    ) : (
                      <Box>
                        <Typography className={classes.roleTitle}>{role.name}</Typography>
                        <Typography className={classes.roleId}>ID: {role.id}</Typography>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box display="flex" alignItems="center" gap={8}>
                      {editingRole?.id === role.id ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateRole(role);
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRole(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit Role">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingRole(role);
                                setEditName(role.name);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Role">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteRole(role);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
        ) : (
          <div className={classes.emptyState}>
            <Typography variant="body2">No roles created yet</Typography>
          </div>
        )}
      </Box>
    </div>
  );
};

export default RoleList;
